import {
  ReferenceDataDefinitions,
  RunParameterDefinitions,
  WorkflowAPI,
} from '@automata/api/apiSchemas';
import { schemas } from '@automata/api/zod';
import { Select, TextField, TrackedButton } from '@automata/ui';
import { getErrorMessage } from '@automata/utils';
import { useFlag } from '@automata/utils/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormControlLabel, Grid2, Stack, Switch } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import isEqual from 'lodash/isEqual';
import { FC, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import zod from 'zod';

import { useSnackbar } from 'hooks/useSnackbar';
import useUserV2 from 'hooks/useUserV2';
import { WorkflowTypes } from 'model/types';
import { PermissionLevelsEnum, PermissionsEnum } from 'model/User';
import { EditorProps } from 'model/WorkflowTypes';
import {
  selectUseWorkcellTransportConfiguration,
  setUseWorkcellTransportConfiguration,
  useWorkflowUiDispatch,
  useWorkflowUiSelector,
} from 'screens/WorkflowsScreen/workflowUiState';

import { initialApiWorkflow } from '../MaestroEditor/defs/initialState';

export type ApiEditorContent = {
  workflow: WorkflowAPI;
  parameters: string;
  referenceData: string;
};

const apiEditorSchema = zod.object({
  workflow: schemas.WorkflowAPI,
  parameters: zod.string(),
  referenceData: zod.string(),
});

enum ConfigurationSource {
  CUSTOM = 'custom',
  WORKCELL = 'workcell',
}

export const ApiEditor: FC<EditorProps> = ({
  requestData,
  updateData,
  onContentUpdated,
}) => {
  const { data } = useUserV2();
  const dispatch = useWorkflowUiDispatch();
  const useWorkcellTransportConfiguration = useWorkflowUiSelector(
    selectUseWorkcellTransportConfiguration
  );

  const isTransportConfigHandlingEnabled = useFlag(
    'fe.transport_config_handling'
  );

  const isUserHaveCreatorAccess = useMemo(
    () =>
      data?.permissions.get(PermissionsEnum.RUN_RESOURCES)?.level ===
      PermissionLevelsEnum.WRITE,
    [data]
  );

  const { enqueueSnackbar } = useSnackbar();
  const { control, formState, handleSubmit, reset, watch } =
    useForm<ApiEditorContent>({
      defaultValues: {
        workflow: initialApiWorkflow,
        parameters: '[]',
        referenceData: '[]',
      },
      resolver: zodResolver(apiEditorSchema),
    });

  useEffect(() => {
    if (requestData?.definition.api) {
      const { override_transport_on_publish = false } =
        requestData.definition.api;

      reset({
        workflow: {
          ...requestData.definition.api,
          override_transport_on_publish,
        },
        parameters: JSON.stringify(
          requestData.attributes?.parameters ?? [],
          null,
          2
        ),
        referenceData: JSON.stringify(
          requestData.attributes?.reference_data_definitions ?? [],
          null,
          2
        ),
      });
    }
  }, [requestData, reset]);

  const isConfigurationSourceVisible =
    requestData?.type === WorkflowTypes.CANVAS ||
    requestData?.type === WorkflowTypes.SDK;
  const configurationSource = useWorkcellTransportConfiguration
    ? ConfigurationSource.WORKCELL
    : ConfigurationSource.CUSTOM;
  const isFormChanged = () => !isEqual(formState.defaultValues, watch());

  const makeUpdate = async (maestro: ApiEditorContent) => {
    if (!maestro?.workflow) {
      enqueueSnackbar('Workflow data not found', { variant: 'error' });
      return;
    }

    let parameters: RunParameterDefinitions;
    try {
      parameters = JSON.parse(maestro.parameters);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'failed to parse parameters'), {
        variant: 'error',
      });
      return;
    }

    let referenceData: ReferenceDataDefinitions;
    try {
      referenceData = JSON.parse(maestro.referenceData);
    } catch (error) {
      enqueueSnackbar(
        getErrorMessage(error, 'failed to parse reference data'),
        { variant: 'error' }
      );
      return;
    }

    try {
      const workflow = await updateData(
        { api: maestro.workflow },
        { parameters, reference_data_definitions: referenceData }
      );
      onContentUpdated(workflow);
      enqueueSnackbar('Workflow updated', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(
        `Workflow failed to update: ${getErrorMessage(error, 'failed')}`,
        { variant: 'error' }
      );
    }
  };

  return (
    <form name="api-editor-form" onSubmit={handleSubmit(makeUpdate)}>
      <Stack mt={1} mb={3} gap={2}>
        {isConfigurationSourceVisible && isTransportConfigHandlingEnabled && (
          <Stack width="fit-content" minWidth={320}>
            <Select
              label="Configuration Source"
              value={configurationSource}
              onChange={(event) =>
                dispatch(
                  setUseWorkcellTransportConfiguration(
                    event.target.value === ConfigurationSource.WORKCELL
                  )
                )
              }
            >
              <MenuItem value={ConfigurationSource.CUSTOM}>
                Custom Configuration
              </MenuItem>
              <MenuItem value={ConfigurationSource.WORKCELL}>
                Workcell Transport Configuration
              </MenuItem>
            </Select>
          </Stack>
        )}

        {(!isConfigurationSourceVisible ||
          !useWorkcellTransportConfiguration) && (
          <>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-end"
              gap={2}
            >
              <Controller
                name="workflow.override_transport_on_publish"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        id="workflow-lock-switch"
                        checked={field.value}
                        disabled={!isUserHaveCreatorAccess}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Don't override"
                  />
                )}
              />
              <TrackedButton
                fullWidth={false}
                loading={false}
                trackLabel="wflow-api-engine-save"
                type="submit"
                variant="contained"
                disabled={
                  !isUserHaveCreatorAccess ||
                  !formState.isValid ||
                  !isFormChanged()
                }
              >
                Save
              </TrackedButton>
            </Stack>

            <Grid2 container rowSpacing={1} columnSpacing={1} paddingBottom={2}>
              <Controller
                name="workflow.cached_maestro_workflow.transport"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    id="scheduler-engine-transport-textfield"
                    disabled={
                      !isUserHaveCreatorAccess ||
                      !watch('workflow.override_transport_on_publish')
                    }
                    label="Transport configuration"
                    labelStyles={{ fontSize: '8px' }}
                    rows={40}
                    sx={{
                      '& textarea': {
                        fontFamily: 'monospace',
                        fontSize: '10px',
                        lineHeight: '1.5',
                      },
                    }}
                  />
                )}
              />
            </Grid2>
          </>
        )}
      </Stack>
    </form>
  );
};
